"use client";

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import {
  AdjustmentsHorizontalIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { ReactNode, useEffect, useRef, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { z } from "zod";
import { freeToolStatuses } from "./available-tools";
import { downloadInBrowser } from "./download-file";
import { imageGeneratorCategories } from "./image-generator-tools";
import { PositionCounter } from "./position-alert";
import { ToolLimitTextLong, ToolLimitTextShort } from "./tool-limit-text";
import { OutOfCreditModal, useFreeTool } from "./use-free-tool";
import {
  frameCostPerImage,
  maxImageCount,
} from "@/app/(signed-in)/create/ai-image-generator/constants";
import {
  businessMonthlyFrames,
  creatorMonthlyFrames,
  proMonthlyFrames,
} from "@/helpers/cost-to-frame-plan";
import { captureClientEvent } from "@/helpers/posthog/client";
import { classNames } from "@/helpers/tailwind-class";
import { trackEvent } from "@/shared/analytics-wrapper";
import { signupFrames, webAssetsCdnBaseUrl } from "@/shared/constants";
import { EmblaCarousel } from "@/shared/embla-carousel";
import { GradientBorder, PlainBorder } from "@/shared/gradient-border";
import { dailyFramesReward } from "@/shared/project-type-metadata";

enum Orientation {
  "square" = "square",
  "landscape" = "landscape",
  "portrait" = "portrait",
}

const sessionStoragePrefix = "ai-image-generator";
const sessionStorageKeys = {
  generatedHistory: `${sessionStoragePrefix}-generatedHistory`,
  prompt: `${sessionStoragePrefix}-prompt`,
  orientation: `${sessionStoragePrefix}-orientation`,
};

const schema = z.object({
  prompt: z.string().min(1, "Please enter a prompt"),
  orientation: z.string(),
  category: z.string().optional(),
  tool: z.string(),
});

type Data = z.infer<typeof schema>;

function TagLine({ text }: { text: ReactNode }) {
  return (
    <div className="flex w-full items-center justify-center">
      <div className="h-px flex-1 bg-gray-700"></div>
      <div className="mx-8 text-center text-sm text-gray-400">{text}</div>
      <div className="h-px flex-1 bg-gray-700"></div>
    </div>
  );
}

type Generated = {
  createdAt: string;
  prompt: string;
  outputs: string[];
};

export function ImageGeneratorForm() {
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: { orientation: "square", prompt: "" },
    reValidateMode: "onSubmit",
  });

  const { tool } = form.watch();

  let carouselIndex: number | undefined = imageGeneratorCategories.findIndex(
    (category) => category.tool === tool
  );
  if (carouselIndex === -1) {
    carouselIndex = undefined;
  }

  const [generatedHistory, setGeneratedHistory] = useState<Generated[]>([]);

  useEffect(() => {
    const history = JSON.parse(sessionStorage.getItem(sessionStorageKeys.generatedHistory) || "[]");
    setGeneratedHistory(history);
  }, []);

  useEffect(() => {
    sessionStorage.setItem(sessionStorageKeys.generatedHistory, JSON.stringify(generatedHistory));
  }, [generatedHistory]);

  const {
    startTask,
    position,
    status,
    outputPath,
    cancelTask,
    reset,
    limit,
    remaining,
    secondsToReset,
    error: apiError,
  } = useFreeTool<Data>({ name: "ai-image-generator", version: "v1" }, { refetchInternalMs: 500 });

  const formId = "ai-image-generator-form";
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (status === freeToolStatuses.success && outputPath) {
      setGeneratedHistory([
        { createdAt: new Date().toISOString(), outputs: outputPath, prompt: form.watch("prompt") },
        ...generatedHistory,
      ]);
    }
  }, [status]);

  useEffect(() => {
    const promptParam = sessionStorage.getItem(sessionStorageKeys.prompt);
    const orientationParam = sessionStorage.getItem(sessionStorageKeys.orientation);

    if (promptParam) {
      form.setValue("prompt", promptParam);
    }
    if (orientationParam) {
      form.setValue("orientation", orientationParam);
    }

    const toolSlug = window.location.pathname.split("/").at(-1);
    form.setValue("tool", toolSlug === "ai-image-generator" ? "general" : (toolSlug ?? "general"));
  }, []);

  const renderContent = () => {
    if (status === freeToolStatuses.error) {
      return (
        <div className="col-span-full flex flex-col gap-2">
          <TagLine text={<div className="flex items-center gap-2">Error</div>} />
          <p className="label-text h-4"> </p>
          <div className="relative mx-auto flex h-full w-full items-center justify-center rounded-md">
            <div role="alert" className="alert w-full border-red-800 bg-red-950/10 p-4">
              <ExclamationTriangleIcon className="text-error h-6 w-6" />
              <span className="">{apiError ?? "An unknown error occurred. Please try again"}</span>
            </div>
          </div>
        </div>
      );
    }

    if (status === freeToolStatuses.pending || status === freeToolStatuses.queued) {
      return (
        <>
          <div className="col-span-full flex flex-col gap-0">
            <TagLine
              text={
                <div className="flex items-center gap-2">
                  <span className="loading loading-spinner loading-xs"></span>
                  Your image is {status === freeToolStatuses.queued ? "queued" : "generating"}{" "}
                </div>
              }
            />

            <p className="label-text h-4 opacity-0">{status}</p>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="relative col-span-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {[1, 2].map((path) => (
                <div
                  key={path}
                  className={classNames(
                    "skeleton aspect-square min-h-40 w-full",
                    path === 2 ? "hidden sm:block" : ""
                  )}
                />
              ))}

              <div
                role="alert"
                className="alert absolute inset-0 mx-auto my-auto h-fit w-11/12 shadow-lg"
              >
                <span className="loading loading-spinner loading-sm text-info"></span>

                {status === freeToolStatuses.queued ? (
                  <div>
                    <h3 className="font-bold">Queuing up your image</h3>
                    <div className="text-xs"></div>
                  </div>
                ) : position !== undefined ? (
                  <PositionCounter
                    position={position}
                    calcEtaSeconds={(position) => position * 5}
                  />
                ) : (
                  <div>
                    <h3 className="font-bold">Your images are generating...</h3>
                    <div className="text-xs">It should take around 3-6 seconds</div>
                  </div>
                )}

                <div>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => {
                      trackEvent({ eventName: "freeAiImageGeneratorCancel", action: "click" });
                      cancelTask();
                    }}
                  >
                    X<span className="sr-only">Cancel</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      );
    }
  };

  const errors = [
    form.formState.errors.prompt?.message,
    form.formState.errors.orientation?.message,
    form.formState.errors.category?.message,
    form.formState.errors.tool?.message,
  ].filter((e) => typeof e === "string");

  const error = errors[0];

  return (
    <div className="flex w-full flex-col gap-6 px-2 lg:px-0">
      <div ref={anchorRef} id="anchor" />
      <FormProvider {...form}>
        <form
          autoComplete="off"
          id={formId}
          onSubmit={form.handleSubmit(
            async (data: Data) => {
              if (anchorRef.current) {
                const { top } = anchorRef.current.getBoundingClientRect();

                if (window.scrollY === 0) {
                  // Check if the user is at the top of the page
                  setTimeout(() => {
                    window.scrollTo({
                      top: top - 60, // to account for the header
                      behavior: "smooth",
                    });
                  }, 100);
                }
              }
              captureClientEvent("ai_image_generated", {});
              trackEvent({
                eventName: "freeAiImageGeneratorRender",
                action: "click",
              });
              startTask(data, true);
            },
            (e) => console.error(e)
          )}
          className="flex w-full flex-col gap-4"
        >
          <div className="relative">
            <EmblaCarousel
              scrollToItemIndex={carouselIndex}
              wrapperClass="overflow-hidden sm:overflow-visible"
              prevButtonClass="-left-8! top-4! size-8! bg-transparent! border-none! shadow-none!"
              nextButtonClass="-right-8! top-4! size-8! bg-transparent! border-none! shadow-none!"
            >
              <div className="flex w-full py-2">
                <div className="shrink-0 px-1">
                  <button
                    onClick={() => {
                      form.setValue("tool", "general");
                      window.history.replaceState({}, "", `/products/ai-image-generator`);
                    }}
                    className={classNames(
                      "btn rounded-full border-2 border-gray-700/70 text-white",
                      form.watch("tool") === "general" ? "btn-primary" : "btn-outline bg-slate-900"
                    )}
                    type="button"
                  >
                    General
                  </button>
                </div>
                {imageGeneratorCategories.map((category) => (
                  <div key={category.tool} className="shrink-0 px-1">
                    <button
                      onClick={() => {
                        form.setValue("tool", category.tool);
                        window.history.replaceState({}, "", `/tools/${category.tool}`);
                      }}
                      className={classNames(
                        "btn no-animation rounded-full border-2 border-gray-700/70 text-white",
                        form.watch("tool") === category.tool
                          ? "btn-primary"
                          : "btn-outline bg-slate-900"
                      )}
                      type="button"
                    >
                      {category.buttonLabel}
                    </button>
                  </div>
                ))}
              </div>
            </EmblaCarousel>
          </div>

          <div className="flex flex-col gap-1">
            <div className="border-primary bg-primary/10 focus-within:border-primary relative flex w-full rounded-lg border shadow-[0_0_12px_rgba(147,51,234,0.5)]">
              <input
                className="grow resize-none border-none bg-transparent px-4 py-3 text-base focus-within:outline-hidden focus:ring-0 focus:outline-hidden sm:text-lg"
                placeholder="Describe what you want to see, e.g. 'epic anime art of wizard'"
                value={form.watch("prompt")}
                onChange={(e) => {
                  const prompt = e.target.value;
                  form.setValue("prompt", prompt);
                  sessionStorage.setItem(sessionStorageKeys.prompt, prompt);
                }}
                disabled={status === freeToolStatuses.pending}
              />
              <div className="hidden items-center gap-2 pr-1.5 sm:flex">
                <OrientationInput showOnMobile={false} />
                <button
                  className="btn btn-primary no-animation rounded-md px-6 text-lg"
                  type="submit"
                  form={formId}
                  disabled={
                    status === freeToolStatuses.pending || status === freeToolStatuses.queued
                  }
                >
                  <SparklesIcon className="mr-2 h-5 w-5" />
                  Generate
                </button>
              </div>
            </div>

            <p className="hidden text-right text-sm text-gray-500 sm:inline-block">
              <ToolLimitTextLong limit={limit} remaining={remaining} unit="image" />
            </p>
          </div>
          <div className="mt-2 flex items-center gap-2 sm:hidden">
            <button
              className="btn btn-primary no-animation flex-1 rounded-md text-base"
              type="submit"
              form={formId}
              disabled={status === freeToolStatuses.pending || status === freeToolStatuses.queued}
            >
              <SparklesIcon className="mr-2 h-5 w-5" />
              Generate
              <span className="text-sm font-light">
                <ToolLimitTextShort limit={limit} remaining={remaining} unit="image" />
              </span>
            </button>

            <OrientationInput showOnMobile={true} />
          </div>
          {error && <div className="mg-1 label-text text-error mt-1">{error}</div>}
        </form>
      </FormProvider>

      <OutOfCreditModal
        open={status === freeToolStatuses.outOfCredit}
        handleClose={() => reset()}
        tool="ai-image-generator"
        limit={limit}
        secondsToReset={secondsToReset}
      />

      <div className="flex flex-col gap-6">
        {renderContent()}

        {generatedHistory.map((g, idx) => {
          return (
            <div className={classNames("grid grid-cols-1 gap-2 sm:grid-cols-2")} key={g.createdAt}>
              <div className="col-span-full flex flex-col gap-0">
                <TagLine text={new Date(g.createdAt).toLocaleString()} />

                <p className="label-text">{g.prompt}</p>
              </div>

              {g.outputs.map((path) => (
                <OutputImage key={path} imageUrl={path} />
              ))}
            </div>
          );
        })}
      </div>
      <div className="flex w-full flex-col gap-4">
        <TagLine text="Explore Sample Images" />
        <AiImageExamples
          tool={tool === "general" ? "ai-image-generator" : tool}
          updatePrompt={(p) => form.setValue("prompt", p)}
        />
      </div>
    </div>
  );
}

function OrientationInput({ showOnMobile }: { showOnMobile: boolean }) {
  const { watch, setValue } = useFormContext();

  const selected = watch("orientation");

  const options = Object.values(Orientation);

  const aspectData = (orientation: (typeof options)[number]) => {
    if (orientation === "square") {
      return {
        ratioText: "1:1",
        style: {
          height: 64,
          width: 64,
        },
      };
    }

    if (orientation === "landscape") {
      return {
        ratioText: "16:9",
        style: {
          height: 64,
          width: 96,
        },
      };
    }

    return {
      ratioText: "9:16",
      style: {
        height: 96,
        width: 64,
      },
    };
  };

  return (
    <Popover
      className={classNames("relative", showOnMobile ? "block sm:hidden" : "hidden sm:block")}
    >
      <PopoverButton className="btn btn-ghost no-animation focus:bg-transparent">
        <AdjustmentsHorizontalIcon className="h-6 w-6" />
      </PopoverButton>
      <PopoverPanel anchor="bottom end" className="flex flex-col rounded-lg bg-slate-950 p-4">
        <div className="mb-2">Aspect Ratio</div>
        <div className="flex flex-row items-center justify-between gap-4 text-sm">
          {options.map((option) => {
            const { ratioText, style } = aspectData(option);

            const Border = selected === option ? GradientBorder : PlainBorder;

            return (
              <button
                className="w-fit cursor-pointer"
                key={option}
                onClick={() => {
                  setValue("orientation", option);
                  sessionStorage.setItem(sessionStorageKeys.orientation, option);
                }}
              >
                <Border borderSize={1}>
                  <div
                    className="flex items-center justify-center rounded-md bg-slate-900 text-center"
                    style={style}
                  >
                    {option}
                    <br />({ratioText})
                  </div>
                </Border>
              </button>
            );
          })}
        </div>
      </PopoverPanel>
    </Popover>
  );
}

function OutputImage({ imageUrl }: { imageUrl: string }) {
  return (
    <div className="skeleton relative flex aspect-square min-h-40 w-full items-center justify-center overflow-hidden rounded-md border border-slate-800">
      <button
        className="btn btn-info btn-sm absolute top-2 right-2"
        onClick={() => {
          trackEvent({
            eventName: "freeAiImageGeneratorDownload",
            action: "click",
          });
          downloadInBrowser(imageUrl);
        }}
      >
        <ArrowDownTrayIcon className="h-5 w-5" /> Download
      </button>

      <img src={imageUrl} key={imageUrl} alt={`Generated Image`} />
    </div>
  );
}

function Benefits() {
  const router = useRouter();
  const { data: session, status } = useSession();

  if (status === "loading") {
    return (
      <div className="border-primary mx-auto flex h-full w-full flex-col gap-6 rounded-xl border-2 p-6">
        <div className="flex items-center justify-between">
          <div className="skeleton h-6 w-full p-0.5"></div>
        </div>

        <div className="grid grid-cols-1 gap-2 text-sm">
          {[1, 2, 3, 4, 5].map((item) => (
            <div key={item} className="flex items-center gap-2">
              <div className="skeleton h-5 w-full"></div>
            </div>
          ))}
        </div>

        <div className="skeleton h-12 w-full"></div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="border-primary mx-auto flex h-full w-full flex-col gap-6 rounded-xl border-2 p-6">
        <div className="flex items-center justify-between">
          <div className="text-lg font-bold">Create An Account</div>
        </div>

        <div className="grid grid-cols-1 gap-2 text-sm">
          {[
            "Higher priority queue",
            `${Math.floor(signupFrames / frameCostPerImage)} free images instantly`,
            `${Math.floor(dailyFramesReward / frameCostPerImage)} daily free images`,
            `Generate ${maxImageCount} images simultaneously`,
            "Access to your image history",
          ].map((item) => (
            <div key={item} className="flex items-center gap-2">
              <CheckIcon className="h-4 w-4 text-indigo-600" />
              <span>{item}</span>
            </div>
          ))}
        </div>

        <button
          className="btn btn-primary no-animation flex"
          onClick={() => {
            trackEvent({ action: "click", eventName: "freeAiImageGeneratorSignUp" });
            router.push("/sign-up?callbackUrl=/create/ai-image-generator");
          }}
        >
          Sign Up Now
        </button>
      </div>
    );
  }

  const tier = session.user.subscriptionTier;

  const upsellContent = {
    free: {
      plan: "Upgrade to creator",
      frames: creatorMonthlyFrames,
    },
    creator: {
      plan: "Upgrade to pro",
      frames: proMonthlyFrames,
    },
    pro: {
      plan: "Upgrade to business",
      frames: businessMonthlyFrames,
    },
    business: {
      plan: "Max Tier Reached!",
      frames: businessMonthlyFrames,
    },
  }[tier];

  return (
    <div className="border-primary mx-auto flex h-full w-full flex-col gap-6 rounded-xl border-2 p-6">
      <div className="flex items-center justify-between">
        <div className="text-lg font-bold capitalize">{upsellContent.plan}</div>
      </div>

      <div className="grid grid-cols-1 gap-2 text-sm">
        {[
          `Generate ${Math.floor(upsellContent.frames / frameCostPerImage).toLocaleString()} images per month`,
          `No watermark`,
          `${tier === "business" ? "Highest" : "Higher"} priority queue`,
          `${Math.floor(dailyFramesReward / frameCostPerImage)} daily free images`,
          `Generate ${maxImageCount} images simultaneously`,
        ].map((item) => (
          <div key={item} className="flex items-center gap-2">
            <CheckIcon className="h-4 w-4 text-indigo-600" />
            <span>{item}</span>
          </div>
        ))}
      </div>

      {tier === "business" ? (
        <button
          className="btn btn-primary no-animation flex"
          onClick={() => {
            trackEvent({ action: "click", eventName: "freeAiImageGeneratorSignUp" });
            router.push("/create/ai-image-generator");
          }}
        >
          Create More
        </button>
      ) : (
        <button
          className="btn btn-primary no-animation flex"
          onClick={() => {
            trackEvent({ action: "click", eventName: "freeAiImageGeneratorPricing" });
            router.push("/pricing");
          }}
        >
          Get Plan
        </button>
      )}
    </div>
  );
}

function AiImageExamples({
  tool,
  updatePrompt,
}: {
  tool: string;
  updatePrompt: (prompt: string) => void;
}) {
  const prompts = [
    { prompt: "Cristiano Ronaldo", imageIdx: 3 },
    { prompt: "Magic Hour", imageIdx: 1 },
    { prompt: "Naruto", imageIdx: 2 },
    { prompt: "Burger King", imageIdx: 4 },
    { prompt: "Iron Man", imageIdx: 5 },
    { prompt: "Batman", imageIdx: 6 },
    { prompt: "son goku, spiky black hair, magic, balloons", imageIdx: 7 },
    { prompt: "Epic anime art of wizard casting a cosmic spell in the sky", imageIdx: 8 },
  ];
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {prompts.map(({ prompt, imageIdx }) => (
        <div
          key={prompt}
          className="skeleton relative aspect-square h-full w-full cursor-pointer overflow-hidden rounded-lg hover:opacity-90"
          onClick={() => {
            updatePrompt(prompt);
            sessionStorage.setItem(sessionStorageKeys.prompt, prompt);
          }}
        >
          {tool && (
            <picture>
              <source
                srcSet={`${webAssetsCdnBaseUrl}/projects/ai-image-generator/art-styles/${tool}${imageIdx}.webp`}
                type="image/webp"
              />
              <source
                srcSet={`${webAssetsCdnBaseUrl}/projects/ai-image-generator/art-styles/${tool}${imageIdx}.jpg`}
                type="image/jpeg"
              />
              <img
                src={`${webAssetsCdnBaseUrl}/projects/ai-image-generator/art-styles/${tool}${imageIdx}.jpg`}
                alt={`Image for ${prompt}`}
              />
            </picture>
          )}
        </div>
      ))}
    </div>
  );
}
